import {
  copyLocaleCodeForPath,
  defaultLocale,
  defaultLocaleCode,
  getLocaleByCode,
  getLocaleFromPath,
  isLocalizedIndexablePath,
  locales,
  localizedPath,
  stripLocaleFromPath,
} from '~/data/locales';
import englishCopy from '#build/page-data/locale/en.mjs';
import { FOUNDING_OFFER } from '~/data/founding-offer';
import localeLoaders from '#build/page-data/locale-loaders.mjs';

export const useLocaleCopyState = () => useState<{ code: string; data: typeof englishCopy } | null>('locale-copy', () => null);
// After the Founding 100 deadline the header banner no longer renders, but a loaded locale's copy
// rides in the page payload of the localized / and /pricing; a build without the offer blanks it.
const withoutFoundingBanner = (data: typeof englishCopy): typeof englishCopy =>
  FOUNDING_OFFER.enabled
    ? data
    : { ...data, copy: { ...data.copy, common: { ...data.copy.common, foundingBannerFull: '', foundingBannerShort: '', foundingBannerCta: '' } } };
export const loadLocaleCopy = async (code: string) =>
  localeLoaders[code] ? withoutFoundingBanner(await localeLoaders[code]()) : englishCopy;

export const useWorkspaceLocale = () => {
  const route = useRoute();
  const requestUrl = import.meta.server ? useRequestURL() : undefined;
  const serverPath = requestUrl?.pathname || '/';
  const serverFullPath = requestUrl
    ? `${requestUrl.pathname}${requestUrl.search}`
    : '/';
  const routePath = computed(() => (import.meta.server ? serverPath : route.path));
  const routeFullPath = computed(() =>
    import.meta.server ? serverFullPath : route.fullPath,
  );

  const currentLocale = computed(() => getLocaleFromPath(routePath.value));
  const currentLocaleCode = computed(() => currentLocale.value.code);
  const basePath = computed(() => stripLocaleFromPath(routeFullPath.value));
  const isDefaultLocale = computed(() => currentLocaleCode.value === defaultLocaleCode);
  const isCurrentPageIndexable = computed(() =>
    isLocalizedIndexablePath(routePath.value, currentLocaleCode.value),
  );
  const canonicalLocaleCode = computed(() =>
    isCurrentPageIndexable.value ? currentLocaleCode.value : defaultLocaleCode,
  );
  // Copy follows the rendered locale, not the URL's: generated editions render
  // English and are translated from the page dictionary (see copyLocaleCodeForPath).
  const copyLocaleCode = computed(() => copyLocaleCodeForPath(routePath.value));
  const copyState = useLocaleCopyState();
  const seoCopy = computed(() => (copyState.value?.data ?? englishCopy).seo);
  const localizedCopy = computed(() => (copyState.value?.data ?? englishCopy).copy);
  const localeUi = computed(() => localizedCopy.value.common);
  const footerCopy = computed(() => localizedCopy.value.footer);
  const homeCopy = computed(() => localizedCopy.value.home);
  const pricingCopy = computed(() => (copyState.value?.data ?? englishCopy).pricing);

  const localePath = (path: string, localeCode = currentLocaleCode.value) =>
    localizedPath(path, localeCode);

  const switchLocale = (localeCode: string) => {
    const targetLocale = getLocaleByCode(localeCode);
    return navigateTo(localePath(basePath.value, targetLocale.code));
  };

  return {
    basePath,
    canonicalLocaleCode,
    copyLocaleCode,
    currentLocale,
    currentLocaleCode,
    defaultLocale,
    isCurrentPageIndexable,
    isDefaultLocale,
    localePath,
    locales,
    footerCopy,
    homeCopy,
    localeUi,
    pricingCopy,
    seoCopy,
    switchLocale,
  };
};
